import React, { useState, useEffect, useRef } from 'react';
import { Search, Users, X, AlertCircle, ChevronDown } from 'lucide-react';
import { getCustomer, Customer, checkDuplicateCustomer, createCustomer, updateCustomerTags } from '../lib/customers';
import { useBusiness } from '../contexts/BusinessContext';
import EmptyState from '../components/EmptyState';
import CustomerModal from '../components/CustomerModal';
import CustomerDetailsModal from '../components/CustomerDetailsModal';
import BookingCustomerInfo from '../components/booking/BookingCustomerInfo';
import PermissionGuard from '../components/PermissionGuard';
import CustomerImportPreviewModal from '../components/CustomerImportPreviewModal';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import { normalizePhoneNumber } from '../services/secondaryContactLists';
import ActiveCustomerBadge from '../components/customers/ActiveCustomerBadge';
import SegmentEditor from '../components/customers/SegmentEditor';
import {
  customersInSegment,
  deleteCustomerSegment,
  listCustomerSegments,
  listSegmentCustomerIds,
  loadSegmentCustomers,
  saveCustomerSegment,
  segmentCountLabel,
  type CustomerSegment,
} from '../lib/customers/segments';

function Customers() {
  const { selectedBusiness } = useBusiness();
  const [searchQuery, setSearchQuery] = useState('');
  const [isNewCustomerModalOpen, setIsNewCustomerModalOpen] = useState(false);
  const [isCustomerDetailsModalOpen, setIsCustomerDetailsModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const PAGE_SIZE = 30;
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [parsedCustomers, setParsedCustomers] = useState<any[]>([]);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [segments, setSegments] = useState<CustomerSegment[]>([]);
  const [segmentTotals, setSegmentTotals] = useState<Record<string, number>>({});
  const [view, setView] = useState<'customers' | 'segments'>('customers');
  const [openSegmentId, setOpenSegmentId] = useState<string | null>(null);
  const [openRows, setOpenRows] = useState<Customer[]>([]);
  const [openTotal, setOpenTotal] = useState(0);
  const [openPage, setOpenPage] = useState(1);
  const [openLoading, setOpenLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingSegment, setEditingSegment] = useState<CustomerSegment | null>(null);
  const [activeHits, setActiveHits] = useState<Set<string>>(new Set());

  const customersSegment = segments.find((s) => s.system_key === 'customers') || null;
  const activeSegment = segments.find((s) => s.system_key === 'active_customers') || null;

  const refreshSegments = async (expandId?: string | null) => {
    if (!selectedBusiness?.id) return;
    const list = await listCustomerSegments(selectedBusiness.id);
    setSegments(list);
    const totals: Record<string, number> = {};
    await Promise.all(list.map(async (segment) => {
      const { total } = await listSegmentCustomerIds(selectedBusiness.id, segment.id, { limit: 1 });
      totals[segment.id] = total;
    }));
    setSegmentTotals(totals);
    if (expandId && list.some((segment) => segment.id === expandId)) {
      setView('segments');
      setOpenSegmentId(expandId);
      setOpenLoading(true);
      try {
        const { rows, total } = await loadSegmentCustomers(selectedBusiness.id, expandId, { page: 1, pageSize: PAGE_SIZE });
        setOpenRows(rows);
        setOpenTotal(total);
        setOpenPage(1);
      } finally {
        setOpenLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!selectedBusiness?.id) return;
    refreshSegments().catch((err) => {
      console.error('Error loading segments:', err);
      setError(err instanceof Error ? err.message : 'Failed to load segments');
      setLoading(false);
    });
  }, [selectedBusiness?.id]);

  const loadCustomers = async () => {
    if (!selectedBusiness?.id || !customersSegment) return;

    try {
      setLoading(true);
      const { rows, total } = await loadSegmentCustomers(selectedBusiness.id, customersSegment.id, {
        page,
        pageSize: PAGE_SIZE,
        search: debouncedSearch,
      });
      setCustomers(rows);
      setTotalCount(total);
      setError(null);
      if (activeSegment?.visible && rows.length > 0) {
        const hits = await customersInSegment(selectedBusiness.id, activeSegment.id, rows.map((c) => c.id));
        setActiveHits(hits);
      } else {
        setActiveHits(new Set());
      }
    } catch (err) {
      console.error('Error loading customers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  // Debounce the search box → server query, and jump back to page 1 on change.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // (Re)load whenever the business, page, search, or segment changes.
  useEffect(() => {
    loadCustomers();
  }, [selectedBusiness?.id, page, debouncedSearch, customersSegment?.id, activeSegment?.visible, activeSegment?.id]);

  const toggleSegment = async (segment: CustomerSegment) => {
    if (openSegmentId === segment.id) {
      setOpenSegmentId(null);
      return;
    }
    if (!selectedBusiness?.id) return;
    setOpenSegmentId(segment.id);
    setOpenRows([]);
    setOpenPage(1);
    setOpenLoading(true);
    try {
      const { rows, total } = await loadSegmentCustomers(selectedBusiness.id, segment.id, { page: 1, pageSize: PAGE_SIZE });
      setOpenRows(rows);
      setOpenTotal(total);
      setSegmentTotals((current) => ({ ...current, [segment.id]: total }));
    } catch (err) {
      console.error('Error loading segment:', err);
      setError(err instanceof Error ? err.message : 'Failed to load this segment');
    } finally {
      setOpenLoading(false);
    }
  };

  const changeOpenPage = async (segment: CustomerSegment, nextPage: number) => {
    if (!selectedBusiness?.id) return;
    setOpenLoading(true);
    try {
      const { rows, total } = await loadSegmentCustomers(selectedBusiness.id, segment.id, { page: nextPage, pageSize: PAGE_SIZE });
      setOpenRows(rows);
      setOpenTotal(total);
      setOpenPage(nextPage);
    } catch (err) {
      console.error('Error loading segment:', err);
      setError(err instanceof Error ? err.message : 'Failed to load this segment');
    } finally {
      setOpenLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  // Separate effect to handle URL parameters after customers are loaded
  useEffect(() => {
    if (customers.length === 0) return;
    
    // Check for customerId in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const customerId = urlParams.get('customerId');
    if (customerId) {
      // Find and open the customer
      const customer = customers.find(c => c.id === customerId);
      if (customer) {
        setSelectedCustomer(customer);
        setIsCustomerDetailsModalOpen(true);
        // Clean up URL
        window.history.replaceState({}, document.title, '/customers');
      } else {
        // If customer isn't found in loaded list, try loading it directly
        const loadCustomer = async () => {
          try {
            const customerData = await getCustomer(customerId);
            if (customerData) {
              setSelectedCustomer(customerData);
              setIsCustomerDetailsModalOpen(true);
              window.history.replaceState({}, document.title, '/customers');
            }
          } catch (err) {
            console.error('Error loading customer from URL:', err);
          }
        };
        loadCustomer();
      }
    }
  }, [customers]);

  // Search + pagination are handled server-side (getCustomersPaged). The list
  // shown is exactly the current page of results.
  const filteredCustomers = customers;
  const listedSegments = segments.filter((segment) =>
    segment.name.toLowerCase().includes(searchQuery.trim().toLowerCase()),
  );

  // Function to download a sample CSV file
  const downloadSampleCSV = () => {
    const sampleData = [
      'first_name,last_name,email,phone,address,status,notes',
      'John,Doe,john.doe@example.com,555-123-4567,"123 Main St, Anytown, USA",active,"First customer"',
      'Jane,Smith,jane.smith@example.com,555-987-6543,"456 Oak St, Somewhere, USA",active,""'
    ].join('\n');
    
    const blob = new Blob([sampleData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', 'sample_customers.csv');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Helper function to detect delimiter
  const detectDelimiter = (text: string): string => {
    const firstLine = text.split('\n')[0];
    
    // Check common delimiters
    const commaCount = (firstLine.match(/,/g) || []).length;
    const semicolonCount = (firstLine.match(/;/g) || []).length;
    const tabCount = (firstLine.match(/\t/g) || []).length;
    
    if (semicolonCount > commaCount && semicolonCount > tabCount) return ';';
    if (tabCount > commaCount && tabCount > semicolonCount) return '\t';
    return ','; // Default to comma
  };

  // Helper function to parse CSV row with possible quoted values
  const parseCSVRow = (row: string, delimiter: string): string[] => {
    const result: string[] = [];
    let inQuote = false;
    let currentValue = '';
    
    for (let i = 0; i < row.length; i++) {
      const char = row[i];
      
      if (char === '"') {
        if (i < row.length - 1 && row[i + 1] === '"') {
          // Handle escaped quotes (double quotes)
          currentValue += '"';
          i++; // Skip the next quote
        } else {
          // Toggle quote state
          inQuote = !inQuote;
        }
      } else if (char === delimiter && !inQuote) {
        result.push(currentValue.trim());
        currentValue = '';
      } else {
        currentValue += char;
      }
    }
    
    // Add the last value
    result.push(currentValue.trim());
    console.log('Parsed row:', result);
    return result;
  };

  // Simpler CSV row parser as fallback
  const simpleSplit = (row: string, delimiter: string): string[] => {
    return row.split(delimiter).map(val => val.trim());
  };

  // Simple, direct import modal handler (no unnecessary wrapping function)
  const toggleImportModal = () => {
    // If we're closing the modal, reset all import-related state
    if (isImportModalOpen) {
      setImportFile(null);
      setIsParsing(false);
      setParsedCustomers([]);
      
      // Also refresh the customer list to show any newly imported customers
      loadCustomers();
    }
    
    // Toggle modal state
    setIsImportModalOpen(!isImportModalOpen);
  };

  // Update the file input handling to use a direct reference
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      console.log('File selected:', file.name, 'Size:', file.size, 'Type:', file.type);
      setImportFile(file);
    } else {
      setImportFile(null);
    }
  };

  // Step 1: Parse CSV with AI
  const handleParseCSV = async () => {
    if (!importFile) {
      toast.error('Please select a CSV file');
      return;
    }

    if (!selectedBusiness?.id) {
      toast.error('No business selected');
      return;
    }

    setIsParsing(true);
    try {
      // Read CSV file content
      const fileContent = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          if (event.target?.result) {
            resolve(event.target.result as string);
          } else {
            reject(new Error('Failed to read file content'));
          }
        };
        reader.onerror = () => reject(new Error('Error reading file'));
        reader.readAsText(importFile, 'UTF-8');
      });

      if (fileContent.trim().length === 0) {
        toast.error('The CSV file is empty');
        setIsParsing(false);
        return;
      }

      // Call AI parser edge function
      console.log('🔄 Calling parse-csv-customers edge function...', {
        businessId: selectedBusiness.id,
        fileSize: fileContent.length,
        fileName: importFile.name
      });

      const { data, error } = await supabase.functions.invoke('parse-csv-customers', {
        body: {
          businessId: selectedBusiness.id,
          csvContent: fileContent
        }
      });

      console.log('📥 Edge function response:', { data, error });

      if (error) {
        // Supabase's FunctionsHttpError hides the server message — pull it out of the response body.
        let serverMessage = error.message;
        try {
          const ctx = (error as any).context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.clone().json().catch(() => null);
            if (body?.error) serverMessage = body.error;
          } else if (ctx && typeof ctx.text === 'function') {
            const txt = await ctx.clone().text().catch(() => '');
            if (txt) serverMessage = txt;
          }
        } catch { /* ignore body read errors */ }
        console.error('❌ Edge function error:', serverMessage, error);
        throw new Error(serverMessage);
      }

      if (data?.insufficientTokens) {
        toast.error(`Insufficient AI tokens. You need at least 1000 tokens. Current balance: ${data.currentBalance}`);
        setIsParsing(false);
        return;
      }

      if (!data?.success || !data?.customers) {
        throw new Error(data?.error || 'Failed to parse CSV');
      }

      if (data.customers.length === 0) {
        toast.error('No valid customers found in CSV. Please check your file format.');
        setIsParsing(false);
        return;
      }

      // Set parsed customers and open preview modal
      console.log('✅ Parsing successful!', {
        customerCount: data.customers.length,
        customers: data.customers,
        partial: data.partial
      });

      setParsedCustomers(data.customers);
      setIsImportModalOpen(false);
      setIsPreviewModalOpen(true);
      
      if (data.partial) {
        toast.warning(`${data.message || 'Partial results'} - ${data.customers.length} customers parsed so far.`);
      } else {
        toast.success(`Parsed ${data.customers.length} customer${data.customers.length !== 1 ? 's' : ''} from CSV`);
      }
    } catch (error) {
      console.error('❌ Error parsing CSV:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to parse CSV file';
      console.error('Error details:', {
        error,
        message: errorMessage,
        stack: error instanceof Error ? error.stack : undefined
      });
      toast.error(errorMessage);
    } finally {
      setIsParsing(false);
    }
  };

  // Step 2: Execute import (called from preview modal)
  const handleExecuteImport = async (customersToImport: any[], tagIds: string[] = []) => {
    if (!selectedBusiness?.id) {
      toast.error('No business selected');
      return;
    }

    if (customersToImport.length === 0) {
      toast.error('No customers to import');
      return;
    }

    setIsImporting(true);
    let imported = 0;
    let duplicates = 0;
    let errors = 0;

    try {
      for (let i = 0; i < customersToImport.length; i++) {
        const customer = customersToImport[i];
        
        try {
          const phoneRaw = customer.phone?.trim();
          const phone = phoneRaw ? normalizePhoneNumber(phoneRaw) : null;

          const newCustomer = await createCustomer({
            business_id: selectedBusiness.id,
            type: 'person',
            first_name: customer.first_name,
            last_name: customer.last_name,
            email: customer.email || null,
            phone,
            address: customer.address || null,
            status: customer.status || 'active',
            notes: customer.notes || null,
            amount_spent: 0,
            credit_balance: 0,
            tags: [],
            preferences: {},
            tax_exempt: false,
            workflow_exempt: false,
            carfax_exempt: false,
            receive_reminders: true,
            receive_offers: true,
            is_supplier: false,
            lifetime_value: 0,
            source: null,
            last_interaction: null,
            next_service_date: null,
            referral_source: null,
            repeat: false,
            lat: null,
            lng: null
          });

          if (tagIds.length > 0 && newCustomer?.id) {
            try {
              await updateCustomerTags(newCustomer.id, tagIds);
            } catch (tagErr) {
              console.error(`Failed to apply tags to customer ${newCustomer.id}:`, tagErr);
            }
          }

          imported++;
        } catch (error: any) {
          if (error?.message?.includes('duplicate') || error?.message?.includes('already exists')) {
            duplicates++;
          } else {
            errors++;
            console.error(`Error importing customer ${i + 1}:`, error);
          }
        }
      }

      // Show summary
      if (imported > 0) {
        const tagNote = tagIds.length > 0 ? ` with ${tagIds.length} tag${tagIds.length !== 1 ? 's' : ''}` : '';
        toast.success(`Successfully imported ${imported} customer${imported !== 1 ? 's' : ''}${tagNote}`);
      }
      if (duplicates > 0) {
        toast.info(`${duplicates} duplicate${duplicates !== 1 ? 's' : ''} skipped`);
      }
      if (errors > 0) {
        toast.error(`${errors} error${errors !== 1 ? 's' : ''} occurred`);
      }

      // Refresh customer list
      await loadCustomers();
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import customers');
    } finally {
      setIsImporting(false);
    }
  };

  // OLD FUNCTION - REPLACED BY TWO-STEP FLOW
  async function handleImportCustomers_OLD() {
    console.log('🔄 [IMPORT] handleImportCustomers called', { 
      importFile: importFile?.name, 
      size: importFile?.size,
      type: importFile?.type,
      selectedBusiness: selectedBusiness?.id 
    });
    
    if (!importFile) {
      console.error('❌ [IMPORT] No file selected');
      toast.error('Please select a CSV file');
      return;
    }
    
    if (!selectedBusiness?.id) {
      console.error('❌ [IMPORT] No business selected');
      toast.error('No business selected');
      return;
    }
    
    try {
      // Reset state for new import
      setImportProgress(0);
      setImportStats({ total: 0, imported: 0, duplicates: 0, errors: 0 });
      setIsImporting(true);
      setImportCompleted(false);
      
      console.log('Starting import process for file:', importFile.name);
      
      // Read the file content
      console.clear(); // Clear console for better debugging
      console.log('🔄 [IMPORT] Reading file content...');
      alert('Import started - check console for detailed logs!');
      const reader = new FileReader();
      
      const fileContent = await new Promise<string>((resolve, reject) => {
        reader.onload = (event) => {
          console.log('✅ [IMPORT] File read successfully');
          if (event.target?.result) {
            resolve(event.target.result as string);
          } else {
            console.error('❌ [IMPORT] No file content in result');
            reject(new Error('Failed to read file content'));
          }
        };
        reader.onerror = (error) => {
          console.error('❌ [IMPORT] FileReader error:', error);
          reject(new Error('Error reading file'));
        };
        reader.readAsText(importFile, 'UTF-8');
      });
      
      console.log('File content loaded, length:', fileContent.length);
      
      if (fileContent.trim().length === 0) {
        toast.error('The CSV file is empty');
        setIsImporting(false);
        setImportCompleted(true);
        setImportStats(prev => ({ ...prev, errors: 1 }));
        return;
      }
      
      // Wrap parsing logic in additional try/catch for better error messages
      try {
        // Split into rows - handle both CR, LF, and CRLF line endings
        const rows = fileContent.split(/\r?\n/).filter(line => line.trim() !== '');
        
        if (rows.length < 1) {
          toast.error('CSV file must have at least one data row');
          setIsImporting(false);
          setImportCompleted(true);
          setImportStats(prev => ({ ...prev, errors: 1 }));
          return;
        }
        
        // Skip direct parser for complex CSV files - use header-based parsing instead
        console.log('🚫 [IMPORT] Skipping direct parser - using header-based parsing for complex CSV');
        const directParseResults: any[] = []; // Force empty to skip direct parsing
        
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (false) { // Disabled - this code is never executed
          if (!selectedBusiness?.id) {
            return; // Early return if no business
          }
          const business = selectedBusiness!;
          console.log('Successfully parsed with direct method:', directParseResults.length, 'customers');
          setImportStats(prev => ({ ...prev, total: directParseResults.length }));
          
          // Process the customers
          const batchSize = 5;
          let imported = 0;
          let duplicates = 0;
          let errors = 0;
          
          for (let i = 0; i < directParseResults.length; i += batchSize) {
            const batch = directParseResults.slice(i, i + batchSize);
            
            for (const customer of batch) {
                          try {
              console.log(`🔄 [IMPORT] Processing customer ${i + 1}/${directParseResults.length}:`, {
                first_name: customer.first_name,
                last_name: customer.last_name,
                email: customer.email,
                phone: customer.phone
              });

              // Check for duplicates - sequential approach
              console.log('🔍 [IMPORT] Checking for duplicates (sequential)...');
              
              let isDuplicate = false;
              let matchingCustomer = null;
              let matchingField = '';
              
              // Step 1: Check phone number first (if provided)
              if (customer.phone?.trim()) {
                console.log('📱 [IMPORT] Checking phone number:', customer.phone);
                const phoneCheck = await checkDuplicateCustomer(
                  business.id,
                  null, // no email check
                  customer.phone
                );
                
                if (phoneCheck.isDuplicate) {
                  isDuplicate = true;
                  matchingCustomer = phoneCheck.matchingCustomer;
                  matchingField = 'phone';
                  console.log('⚠️ [IMPORT] Phone number already exists in system');
                }
              }
              
              // Step 2: If phone didn't match, check email (if provided)
              if (!isDuplicate && customer.email?.trim()) {
                console.log('📧 [IMPORT] Checking email address:', customer.email);
                const emailCheck = await checkDuplicateCustomer(
                  business.id,
                  customer.email,
                  null // no phone check
                );
                
                if (emailCheck.isDuplicate) {
                  isDuplicate = true;
                  matchingCustomer = emailCheck.matchingCustomer;
                  matchingField = 'email';
                  console.log('⚠️ [IMPORT] Email address already exists in system');
                }
              }
              
              if (isDuplicate) {
                console.log('⚠️ [IMPORT] Duplicate customer detected:', {
                  new_customer: `${customer.first_name} ${customer.last_name}`,
                  existing_customer: `${matchingCustomer?.first_name} ${matchingCustomer?.last_name}`,
                  matching_field: matchingField,
                  matching_value: matchingField === 'phone' ? customer.phone : customer.email
                });
                duplicates++;
                setImportStats(prev => ({ ...prev, duplicates }));
                continue;
              }
              
              console.log('✅ [IMPORT] No duplicates found, creating customer...');
              
              // Create the customer
              const customerData = {
                business_id: business.id,
                type: 'person' as const,
                first_name: customer.first_name,
                last_name: customer.last_name, 
                email: customer.email,
                phone: customer.phone,
                address: null,
                status: 'active' as const,
                amount_spent: 0,
                credit_balance: 0,
                tags: [],
                preferences: {},
                tax_exempt: false,
                workflow_exempt: false,
                carfax_exempt: false,
                receive_reminders: true,
                receive_offers: true,
                is_supplier: false,
                lifetime_value: 0,
                source: null,
                last_interaction: null,
                next_service_date: null,
                referral_source: null,
                notes: null,
                repeat: false,
                lat: null,
                lng: null
              };
              
              console.log('📝 [IMPORT] Customer data prepared:', customerData);
              
              await createCustomer(customerData);
              
              console.log('✅ [IMPORT] Successfully imported customer:', customer.first_name, customer.last_name);
              imported++;
              setImportStats(prev => ({ ...prev, imported }));
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : String(error);
              const errorStack = error instanceof Error ? error.stack : undefined;
              console.error('❌ [IMPORT] Error importing customer:', {
                customer: `${customer.first_name} ${customer.last_name}`,
                email: customer.email,
                phone: customer.phone,
                error: errorMessage,
                stack: errorStack
              });
              errors++;
              setImportStats(prev => ({ ...prev, errors }));
            }
            }
            
            const progress = Math.min(100, Math.round(((i + batch.length) / directParseResults.length) * 100));
            setImportProgress(progress);
            
            // Short delay to allow UI to update
            await new Promise(resolve => setTimeout(resolve, 50));
          }
          
          // Final update
          setImportStats({ total: directParseResults.length, imported, duplicates, errors });
          setImportProgress(100);
          
          // Show success message
          if (imported > 0) {
            toast.success(`Imported ${imported} customers successfully`);
          } else if (duplicates > 0 && errors === 0) {
            toast.info('All customers already exist in the system');
          } else if (errors > 0) {
            toast.error(`Failed to import ${errors} customers`);
          }
          
          // Refresh customer list
          await loadCustomers();
          
          // Always show the import summary
          setIsImporting(false);
          setImportCompleted(true);
          return;
        }
        
        // If direct parsing didn't work, continue with the regular approach
        // Detect delimiter if not explicitly set
        const detectedDelimiter = detectDelimiter(fileContent);
        const delimiter = csvDelimiter || detectedDelimiter;
        
        console.log('Using delimiter:', delimiter === ',' ? 'comma' : delimiter === ';' ? 'semicolon' : 'tab');
        
        // Try parsing header with the complex parser, fall back to simple if needed
        let headerRow;
        try {
          headerRow = parseCSVRow(rows[0], delimiter);
        } catch (e) {
          console.error('Error parsing header with complex parser, using fallback:', e);
          headerRow = simpleSplit(rows[0], delimiter);
        }
        
        console.log('🎯 [CSV PARSE] Raw header row:', headerRow);
        console.log('🎯 [CSV PARSE] Header row length:', headerRow.length);
        
        // Map standard column names to indices
        const columnMap: Record<string, number> = {};
        
        // Check for specific CRM export format first
        const headerString = headerRow.join(',').toLowerCase();
        console.log('🔍 [CSV PARSE] Header string for detection:', headerString);
        console.log('🔍 [CSV PARSE] Looking for: contact id, first name, last name');
        
        if (headerString.includes('contact id') && headerString.includes('first name') && headerString.includes('last name')) {
          console.log('🎯 [CSV PARSE] ✅ CRM export format DETECTED - using hardcoded mapping');
          // Hardcoded mapping for CRM export format:
          // "Contact Id","First Name","Last Name","Business Name","Company Name","Phone","Email","Created","Last Activity","Tags","Additional Emails","Additional Phones"
          columnMap.contact_id = 0;
          columnMap.first_name = 1;
          columnMap.last_name = 2;
          columnMap.business_name = 3;
          columnMap.company_name = 4;
          columnMap.phone = 5;
          columnMap.email = 6;
          console.log('📱 [CSV PARSE] CRM Format - Phone mapped to column 5');
          console.log('📧 [CSV PARSE] CRM Format - Email mapped to column 6');
          console.log('🗺️ [CSV PARSE] Final column mapping:', columnMap);
        } else {
          console.log('❌ [CSV PARSE] CRM format NOT detected, using dynamic mapping');
          // Enhanced header mapping for various CSV formats
          headerRow.forEach((header, index) => {
            const normalizedHeader = header.toLowerCase().trim().replace(/['"]/g, ''); // Remove quotes
            
            console.log(`🔍 [CSV PARSE] Header ${index}: "${header}" -> "${normalizedHeader}"`);
            
            // Check for common column name patterns
            if (normalizedHeader.includes('first') && normalizedHeader.includes('name')) {
              columnMap.first_name = index;
              console.log(`✅ [CSV PARSE] Mapped first_name to column ${index}`);
            } else if (normalizedHeader.includes('last') && normalizedHeader.includes('name')) {
              columnMap.last_name = index;
              console.log(`✅ [CSV PARSE] Mapped last_name to column ${index}`);
            } else if (normalizedHeader === 'email' || normalizedHeader.includes('email')) {
              columnMap.email = index;
              console.log(`✅ [CSV PARSE] Mapped email to column ${index}`);
            } else if (normalizedHeader === 'phone' || normalizedHeader.includes('phone')) {
              columnMap.phone = index;
              console.log(`✅ [CSV PARSE] Mapped phone to column ${index}`);
            } else if (normalizedHeader.includes('address')) {
              columnMap.address = index;
              console.log(`✅ [CSV PARSE] Mapped address to column ${index}`);
            } else if (normalizedHeader.includes('status')) {
              columnMap.status = index;
              console.log(`✅ [CSV PARSE] Mapped status to column ${index}`);
            } else if (normalizedHeader.includes('note')) {
              columnMap.notes = index;
              console.log(`✅ [CSV PARSE] Mapped notes to column ${index}`);
            } else if (normalizedHeader.includes('business') && normalizedHeader.includes('name')) {
              columnMap.business_name = index;
              console.log(`✅ [CSV PARSE] Mapped business_name to column ${index}`);
            } else if (normalizedHeader.includes('company') && normalizedHeader.includes('name')) {
              columnMap.company_name = index;
              console.log(`✅ [CSV PARSE] Mapped company_name to column ${index}`);
            }
          });
        }
        
        console.log('Column mapping:', columnMap);
        
        // If we don't have proper column mapping, try to detect if this is a simple name,email format
        // Like: Name,email,phone
        if (!('first_name' in columnMap) || !('last_name' in columnMap)) {
          console.log('Standard columns not found, trying alternative format detection');
          
          // Check if we have rows with email addresses in them
          let emailColumnIndex = -1;
          let nameColumnIndex = -1;
          let phoneColumnIndex = -1;
          
          // Look at the first few data rows to detect patterns
          for (let i = 1; i < Math.min(rows.length, 5); i++) {
            const columns = parseCSVRow(rows[i], delimiter);
            for (let j = 0; j < columns.length; j++) {
              const value = columns[j].toLowerCase().trim();
              if (value.includes('@') && value.includes('.')) {
                emailColumnIndex = j;
              } else if (value.includes('604-') || value.includes('778-') || value.match(/\d{3}-\d{3}-\d{4}/)) {
                phoneColumnIndex = j;
              }
            }
            if (emailColumnIndex !== -1) break;
          }
          
          // If we found an email column, assume the format is name,email,phone
          // or name,email (comma delimited)
          if (emailColumnIndex !== -1) {
            console.log('Detected alternative format with email at index', emailColumnIndex);
            // Assume name is before email
            nameColumnIndex = emailColumnIndex > 0 ? emailColumnIndex - 1 : -1;
            
            // If we couldn't find a phone column but there's a column after email, assume it's phone
            if (phoneColumnIndex === -1 && emailColumnIndex < headerRow.length - 1) {
              phoneColumnIndex = emailColumnIndex + 1;
            }
            
            // Update our column map
            columnMap.combined_name_email = emailColumnIndex;
            if (phoneColumnIndex !== -1) {
              columnMap.phone = phoneColumnIndex;
            }
          }
        }
        
        // Handle special case where the file doesn't have a header row
        // and is just data with name,email,phone format
        if (Object.keys(columnMap).length === 0 && rows.length > 0) {
          // Check if first row looks like data rather than headers
          const firstRow = parseCSVRow(rows[0], delimiter);
          let hasEmailInFirstRow = false;
          
          for (const cell of firstRow) {
            if (cell.includes('@') && cell.includes('.')) {
              hasEmailInFirstRow = true;
              break;
            }
          }
          
          if (hasEmailInFirstRow) {
            console.log('File appears to have no header row, using first row for format detection');
            for (let j = 0; j < firstRow.length; j++) {
              const value = firstRow[j].toLowerCase().trim();
              if (value.includes('@') && value.includes('.')) {
                columnMap.combined_name_email = j;
              } else if (value.includes('604-') || value.includes('778-') || value.match(/\d{3}-\d{3}-\d{4}/)) {
                columnMap.phone = j;
              }
            }
          }
        }
        
        console.log('🗺️ [CSV PARSE] Final column mapping:', columnMap);
        
        // Show a sample row to verify column alignment
        if (rows.length > 1) {
          const sampleRow = parseCSVRow(rows[1], delimiter);
          console.log('📋 [CSV PARSE] Sample data row:', sampleRow);
          console.log('📋 [CSV PARSE] Column analysis:');
          sampleRow.forEach((value, index) => {
            console.log(`  Column ${index}: "${value}"`);
          });
          
          if ('phone' in columnMap) {
            console.log(`📱 [CSV PARSE] Phone column ${columnMap.phone} contains: "${sampleRow[columnMap.phone]}"`);
          }
          if ('email' in columnMap) {
            console.log(`📧 [CSV PARSE] Email column ${columnMap.email} contains: "${sampleRow[columnMap.email]}"`);
          }
        }
        
        // Ensure we have at least one way to identify customers
        if (!('first_name' in columnMap) && !('combined_name_email' in columnMap)) {
          toast.error('CSV format not recognized. Please ensure it contains name and email columns.');
          setIsImporting(false);
          setImportCompleted(true);
          setImportStats(prev => ({ ...prev, errors: 1 }));
          return;
        }
        
        // Process data rows - skip header
        const customersToImport = [];
        for (let i = 1; i < rows.length; i++) {
          if (!rows[i].trim()) continue; // Skip empty rows
          
          try {
            const columns = parseCSVRow(rows[i], delimiter);
            // Skip row if it doesn't have enough columns
            if (columns.length === 0) {
              console.log('Skipping empty row');
              continue;
            }
            
            let firstName = '';
            let lastName = '';
            let email = null;
            let phone = null;
            
            // Handle the standard column format
            if ('first_name' in columnMap && 'last_name' in columnMap) {
              firstName = columns[columnMap.first_name]?.trim() || '';
              lastName = columns[columnMap.last_name]?.trim() || '';
              
              // If first/last name are empty, try business/company names
              if (!firstName && !lastName) {
                if ('business_name' in columnMap && columns[columnMap.business_name]?.trim()) {
                  const businessName = columns[columnMap.business_name].trim();
                  firstName = businessName;
                  lastName = '(Business)';
                  console.log(`📢 [CSV PARSE] Using business name: ${businessName}`);
                } else if ('company_name' in columnMap && columns[columnMap.company_name]?.trim()) {
                  const companyName = columns[columnMap.company_name].trim();
                  firstName = companyName;
                  lastName = '(Company)';
                  console.log(`🏢 [CSV PARSE] Using company name: ${companyName}`);
                } else {
                  console.log('⚠️ [CSV PARSE] Skipping row due to missing name data:', rows[i]);
                  continue;
                }
              }
              
              // If only one name is provided, split or use as-is
              if (!firstName && lastName) {
                firstName = lastName;
                lastName = '(Name)';
              } else if (firstName && !lastName) {
                // Try to split if it contains spaces
                const nameParts = firstName.split(' ');
                if (nameParts.length > 1) {
                  firstName = nameParts[0];
                  lastName = nameParts.slice(1).join(' ');
                } else {
                  lastName = '(Single Name)';
                }
              }
              
              email = 'email' in columnMap && columns[columnMap.email] ? columns[columnMap.email].trim() || null : null;
              phone = 'phone' in columnMap && columns[columnMap.phone] ? columns[columnMap.phone].trim() || null : null;
            } 
            // Handle the combined name/email format
            else if ('combined_name_email' in columnMap) {
              const emailValue = columns[columnMap.combined_name_email]?.trim() || '';
              
              if (!emailValue || !emailValue.includes('@')) {
                console.log('Skipping row due to missing or invalid email:', rows[i]);
                continue;
              }
              
              // Extract email from the combined field
              email = emailValue;
              
              // Extract name from beginning of the line
              // Format appears to be: Name,email
              if (columnMap.combined_name_email > 0) {
                const nameValue = columns[columnMap.combined_name_email - 1]?.trim() || '';
                
                // Try to split name into first and last
                const nameParts = nameValue.split(',');
                if (nameParts.length > 1) {
                  firstName = nameParts[0].trim();
                  lastName = nameParts[1].trim();
                } else {
                  // If there's no comma, try to split on space
                  const spaceParts = nameValue.split(/\s+/);
                  if (spaceParts.length > 1) {
                    firstName = spaceParts[0].trim();
                    lastName = spaceParts.slice(1).join(' ').trim();
                  } else {
                    // If all else fails, use the whole value as first name
                    firstName = nameValue;
                    lastName = '(No Last Name)';
                  }
                }
              } else {
                // If there's no name column, extract from email
                firstName = email.split('@')[0];
                lastName = '(From Email)';
              }
              
              // Get phone if available
              phone = 'phone' in columnMap && columns[columnMap.phone] ? columns[columnMap.phone].trim() || null : null;
            } 
            // Special case: combined name and email in one column (like "name,email@example.com")
            else {
              // Try to find an email in any column
              let foundEmail = false;
              
              for (let j = 0; j < columns.length; j++) {
                const value = columns[j].trim();
                if (value.includes('@') && value.includes('.')) {
                  email = value;
                  foundEmail = true;
                  
                  // If this is a line like "name,email@example.com,phone"
                  // Try to extract name from same column
                  const commaIdx = value.indexOf(',');
                  if (commaIdx > 0 && commaIdx < value.indexOf('@')) {
                    const namePart = value.substring(0, commaIdx).trim();
                    const emailPart = value.substring(commaIdx + 1).trim();
                    
                    email = emailPart;
                    const nameParts = namePart.split(/\s+/);
                    if (nameParts.length > 1) {
                      firstName = nameParts[0].trim();
                      lastName = nameParts.slice(1).join(' ').trim();
                    } else {
                      firstName = namePart;
                      lastName = '(No Last Name)';
                    }
                  } else {
                    // Just use email as identifier
                    firstName = email.split('@')[0];
                    lastName = '(From Email)';
                  }
                  
                  // Try to find phone in another column
                  for (let k = 0; k < columns.length; k++) {
                    if (k !== j) {
                      const phoneCandidate = columns[k].trim();
                      if (phoneCandidate.includes('-') || /^\d+$/.test(phoneCandidate)) {
                        phone = phoneCandidate;
                        break;
                      }
                    }
                  }
                  
                  break;
                }
              }
              
              if (!foundEmail) {
                console.log('Skipping row due to no email found:', rows[i]);
                continue;
              }
            }
            
            // Handle special case for the file shown in screenshot
            // Format appears to be: Name,email,phone with no header row
            if (rows.length > 10 && i < 10 && !phone && columns.length >= 2) {
              const line = rows[i];
              // Check if it matches pattern like: Name,email@example.com,604-xxx-xxxx
              const emailPhoneMatch = line.match(/([^,]+),([^,]+@[^,]+),([^,]+)/);
              if (emailPhoneMatch) {
                const [_, namePart, emailPart, phonePart] = emailPhoneMatch;
                
                // Update with the extracted values
                if (emailPart.includes('@') && emailPart.includes('.')) {
                  email = emailPart.trim();
                }
                
                if (phonePart.includes('-') || /^\d+$/.test(phonePart)) {
                  phone = phonePart.trim();
                }
                
                const nameParts = namePart.split(/\s+/);
                if (nameParts.length > 1) {
                  firstName = nameParts[0].trim();
                  lastName = nameParts.slice(1).join(' ').trim();
                } else {
                  firstName = namePart.trim();
                  lastName = '(No Last Name)';
                }
              }
            }
            
            // Ensure we have minimum required fields
            if (!firstName || !email) {
              console.log('Skipping row due to missing required fields:', rows[i]);
              continue;
            }
            
            customersToImport.push({
              first_name: firstName,
              last_name: lastName,
              email: email,
              phone: phone,
              address: 'address' in columnMap && columns[columnMap.address] ? columns[columnMap.address].trim() || null : null,
              status: 'status' in columnMap && columns[columnMap.status] ? columns[columnMap.status].trim() as 'active' | 'inactive' : 'active',
              notes: 'notes' in columnMap && columns[columnMap.notes] ? columns[columnMap.notes].trim() || null : null
            });
          } catch (rowError) {
            console.error('Error parsing row:', rows[i], rowError);
            continue;
          }
        }
        
        console.log('Valid customers to import:', customersToImport.length);
        setImportStats(prev => ({ ...prev, total: customersToImport.length }));
        
        if (customersToImport.length === 0) {
          toast.error('No valid customers found in CSV');
          setIsImporting(false);
          setImportCompleted(true);
          setImportStats(prev => ({ ...prev, errors: 1 }));
          return;
        }
        
        // Process customers in batches to show progress
        const batchSize = 5;
        let imported = 0;
        let duplicates = 0;
        let errors = 0;
        
        for (let i = 0; i < customersToImport.length; i += batchSize) {
          const batch = customersToImport.slice(i, i + batchSize);
          
          for (const customer of batch) {
            try {
              console.log(`🔄 [IMPORT] Processing customer:`, {
                first_name: customer.first_name,
                last_name: customer.last_name,
                email: customer.email,
                phone: customer.phone,
                status: customer.status
              });

              // Validation checks
              if (!customer.first_name || !customer.first_name.trim()) {
                throw new Error('First name is required and cannot be empty');
              }
              if (!customer.last_name || !customer.last_name.trim()) {
                throw new Error('Last name is required and cannot be empty');
              }
              if (!customer.email && !customer.phone) {
                throw new Error('Either email or phone number is required');
              }

              // Normalize phone to E.164 for duplicate check and create (matches all_messages format)
              const phoneRaw = customer.phone?.trim();
              const phoneNormalized = phoneRaw ? normalizePhoneNumber(phoneRaw) : null;

              // Check for duplicates - sequential approach
              console.log('🔍 [IMPORT] Checking for duplicates (sequential)...');
              
              let isDuplicate = false;
              let matchingCustomer = null;
              let matchingField = '';
              
              // Step 1: Check phone number first (if provided) using normalized form
              if (phoneNormalized) {
                console.log('📱 [IMPORT] Checking phone number:', phoneNormalized);
                const phoneCheck = await checkDuplicateCustomer(
                  selectedBusiness.id,
                  null, // no email check
                  phoneNormalized
                );
                
                if (phoneCheck.isDuplicate) {
                  isDuplicate = true;
                  matchingCustomer = phoneCheck.matchingCustomer;
                  matchingField = 'phone';
                  console.log('⚠️ [IMPORT] Phone number already exists in system');
                }
              }
              
              // Step 2: If phone didn't match, check email (if provided)
              if (!isDuplicate && customer.email?.trim()) {
                console.log('📧 [IMPORT] Checking email address:', customer.email);
                const emailCheck = await checkDuplicateCustomer(
                  selectedBusiness.id,
                  customer.email,
                  null // no phone check
                );
                
                if (emailCheck.isDuplicate) {
                  isDuplicate = true;
                  matchingCustomer = emailCheck.matchingCustomer;
                  matchingField = 'email';
                  console.log('⚠️ [IMPORT] Email address already exists in system');
                }
              }
              
              if (isDuplicate) {
                console.log('⚠️ [IMPORT] Duplicate customer detected:', {
                  new_customer: `${customer.first_name} ${customer.last_name}`,
                  existing_customer: `${matchingCustomer?.first_name} ${matchingCustomer?.last_name}`,
                  matching_field: matchingField,
                  matching_value: matchingField === 'phone' ? customer.phone : customer.email
                });
                duplicates++;
                setImportStats(prev => ({ ...prev, duplicates }));
                continue;
              }
              
              console.log('✅ [IMPORT] No duplicates found, creating customer...');

              // Create the customer with explicit properties to avoid undefined values
              const customerData = {
                business_id: selectedBusiness.id,
                type: 'person' as const,
                first_name: customer.first_name.trim(),
                last_name: customer.last_name.trim(),
                email: customer.email?.trim() || null,
                phone: phoneNormalized,
                address: customer.address?.trim() || null,
                status: (customer.status?.trim() as 'active' | 'inactive') || 'active',
                amount_spent: 0,
                credit_balance: 0,
                tags: [],
                preferences: {},
                tax_exempt: false,
                workflow_exempt: false,
                carfax_exempt: false,
                receive_reminders: true,
                receive_offers: true,
                is_supplier: false,
                lifetime_value: 0,
                source: null,
                last_interaction: null,
                next_service_date: null,
                referral_source: null,
                notes: customer.notes?.trim() || null,
                repeat: false,
                lat: null,
                lng: null
              };
              
              console.log('📝 [IMPORT] Customer data prepared:', customerData);
              
              await createCustomer(customerData);
              
              console.log('✅ [IMPORT] Successfully imported customer:', customer.first_name, customer.last_name);
              imported++;
              setImportStats(prev => ({ ...prev, imported }));
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : String(error);
              const errorStack = error instanceof Error ? error.stack : undefined;
              console.error('❌ [IMPORT] Error importing customer:', {
                customer: `${customer.first_name} ${customer.last_name}`,
                email: customer.email,
                phone: customer.phone,
                error: errorMessage,
                stack: errorStack
              });
              errors++;
              setImportStats(prev => ({ ...prev, errors }));
            }
          }
          
          const progress = Math.min(100, Math.round(((i + batch.length) / customersToImport.length) * 100));
          setImportProgress(progress);
          
          // Short delay to allow UI to update
          await new Promise(resolve => setTimeout(resolve, 50));
        }
        
        // Final update
        setImportStats({ total: customersToImport.length, imported, duplicates, errors });
        setImportProgress(100);
        
        // Show success message
        if (imported > 0) {
          toast.success(`Imported ${imported} customers successfully`);
        } else if (duplicates > 0 && errors === 0) {
          toast.info('All customers already exist in the system');
        } else if (errors > 0) {
          toast.error(`Failed to import ${errors} customers`);
        }
        
        // Refresh customer list
        await loadCustomers();
        
        // Always show the import summary
        setIsImporting(false);
        setImportCompleted(true);
      } catch (parsingError) {
        // Handle CSV parsing errors specifically
        console.error('Error parsing CSV:', parsingError);
        toast.error('Failed to parse CSV: ' + (parsingError instanceof Error ? parsingError.message : 'Invalid format'));
        setIsImporting(false);
        setImportCompleted(true);
        setImportStats(prev => ({ ...prev, errors: 1 }));
      }
    } catch (error) {
      console.error('❌ [IMPORT] Error processing CSV import:', error);
      
      // Provide specific error messages based on error type
      let errorMessage = 'Failed to import customers: ';
      
      if (error instanceof Error) {
        if (error.message.includes('Failed to read file')) {
          errorMessage += 'Could not read the selected file. Please check the file format.';
        } else if (error.message.includes('CSV format not recognized')) {
          errorMessage += 'CSV format not recognized. Please check your file format.';
        } else if (error.message.includes('duplicate')) {
          errorMessage += 'Duplicate customer found: ' + error.message;
        } else if (error.message.includes('Supabase') || error.message.includes('database')) {
          errorMessage += 'Database connection error. Please try again.';
        } else {
          errorMessage += error.message;
        }
      } else {
        errorMessage += 'Unknown error occurred';
      }
      
      toast.error(errorMessage);
      setIsImporting(false);
      setImportCompleted(true);
      setImportStats(prev => ({ ...prev, errors: 1 }));
    }
  }

  // Parse CSV text directly (for the specific format in the screenshot)
  const parseSimpleCSVFormat = (text: string): { first_name: string; last_name: string; email: string; phone: string | null }[] => {
    console.log('Trying direct parsing of simple CSV format');
    const results = [];
    const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
    
    for (const line of lines) {
      // Look for patterns like: Name,email@example.com,phone
      // Or Firstname Lastname,email@example.com,phone
      const match = line.match(/([^,]+),([^,]+@[^,]+),(.*)/);
      
      if (match) {
        const [_, namePart, emailPart, phonePart] = match;
        
        if (!emailPart.includes('@')) continue;
        
        // Extract name parts - could be "First Last" or just "Name"
        let firstName = '';
        let lastName = '';
        
        const nameParts = namePart.split(/\s+/);
        if (nameParts.length > 1) {
          firstName = nameParts[0].trim();
          lastName = nameParts.slice(1).join(' ').trim();
        } else {
          firstName = namePart.trim();
          lastName = '(From Email)';
        }
        
        results.push({
          first_name: firstName,
          last_name: lastName,
          email: emailPart.trim(),
          phone: phonePart.trim() || null
        });
      }
    }
    
    return results;
  };

  return (
    <div 
      className="min-h-screen bg-gray-50" 
      data-customers-page
      style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
      }}
    >
      <div className="w-full px-4 sm:px-6 py-4 sm:py-6">
        <div className="space-y-4 sm:space-y-6">
          {/* Header */}
          <div className="space-y-3 sm:space-y-4">
            {/* Title */}
            <div className="flex items-center gap-3">
              <h1 
                className="text-2xl font-bold text-gray-900"
                style={{ letterSpacing: '-0.01em' }}
              >
                Customers
              </h1>
              {view === 'customers' && !loading && (
                <span 
                  className="text-[15px] text-gray-600 font-normal"
                  style={{ letterSpacing: '-0.01em' }}
                >
                  ({totalCount} {totalCount === 1 ? 'customer' : 'customers'})
                </span>
              )}
              {view === 'segments' && (
                <span
                  className="text-[15px] text-gray-600 font-normal"
                  style={{ letterSpacing: '-0.01em' }}
                >
                  ({segments.length} {segments.length === 1 ? 'segment' : 'segments'})
                </span>
              )}
            </div>
            
            {/* Search, Filter, and Actions Row */}
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Search Bar */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={view === 'segments' ? 'Search segments' : 'Search customers'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2.5 text-[15px] bg-white border border-gray-200/60 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full shadow-sm"
                  style={{ 
                    minHeight: '44px',
                    letterSpacing: '-0.01em',
                    WebkitTapHighlightColor: 'transparent',
                    touchAction: 'manipulation'
                  }}
                />
              </div>
              
              {/* Add Button */}
              <button 
                onClick={() => setIsNewCustomerModalOpen(true)}
                className="bg-blue-600 active:bg-blue-700 text-white px-4 py-2.5 rounded-[10px] font-semibold transition-all duration-150 whitespace-nowrap flex-shrink-0 shadow-sm touch-manipulation"
                style={{ 
                  minHeight: '44px',
                  WebkitTapHighlightColor: 'transparent',
                  letterSpacing: '-0.01em',
                  fontSize: '15px'
                }}
              >
                <span className="sm:hidden">Add</span>
                <span className="hidden sm:inline">Add Customer</span>
              </button>
              
              {/* Import Button */}
              <button 
                onClick={() => setIsImportModalOpen(true)}
                className="bg-gray-600 active:bg-gray-700 text-white px-4 py-2.5 rounded-[10px] font-semibold transition-all duration-150 whitespace-nowrap flex-shrink-0 shadow-sm touch-manipulation"
                style={{ 
                  minHeight: '44px',
                  WebkitTapHighlightColor: 'transparent',
                  letterSpacing: '-0.01em',
                  fontSize: '15px'
                }}
              >
                <span className="sm:hidden">Import</span>
                <span className="hidden sm:inline">Import</span>
              </button>
            </div>

            <div className="grid grid-cols-2 rounded-[12px] bg-gray-100 p-1">
              {([
                ['customers', 'All Customers'],
                ['segments', 'Segments'],
              ] as const).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => {
                    setView(id);
                    setSearchQuery('');
                  }}
                  className={`min-h-[40px] rounded-[10px] text-[15px] font-semibold transition-all duration-150 touch-manipulation ${
                    view === id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                  }`}
                  style={{ WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-[12px] text-[15px]">
            <div className="flex items-center">
              <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
              {error}
            </div>
          </div>
        )}

        {view === 'customers' ? (
        <div className="space-y-3">
          {loading ? (
            <div className="flex items-center justify-center h-64 bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
              <div className="text-center">
                <div className="w-12 h-12 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                <p className="mt-4 text-[15px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>Loading customers...</p>
              </div>
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="flex items-center justify-center h-64 bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
              <EmptyState
                icon={<Users className="w-8 h-8 text-gray-400" />}
                title={searchQuery ? 'No matching customers' : 'No customers yet'}
                description={
                  searchQuery
                    ? 'Try adjusting your search terms'
                    : 'Start adding customers to manage your client base.'
                }
                actionLabel={
                  searchQuery ? 'Clear search' : 'Add your first customer'
                }
                onAction={() => (searchQuery ? setSearchQuery('') : setIsNewCustomerModalOpen(true))}
              />
            </div>
          ) : (
            <div className="space-y-3">
              {/* Customer List - Using BookingCustomerInfo Component */}
              {filteredCustomers.map((customer) => (
                <div
                  key={customer.id}
                  onClickCapture={(e) => {
                    // Ignore clicks from portal-rendered elements (e.g. CustomerMessageModal)
                    if (!e.currentTarget.contains(e.target as Node)) {
                      return;
                    }
                    
                    // Use capture phase to catch clicks before they're stopped by child elements
                    const target = e.target as HTMLElement;
                    
                    // Check if clicking directly on a button element or an SVG icon inside a button
                    const clickedButton = target.closest('button');
                    const isButton = target.tagName === 'BUTTON' || 
                                    clickedButton ||
                                    (target.tagName === 'svg' && clickedButton) ||
                                    (target.tagName === 'path' && clickedButton);
                    // Check if clicking on source dropdown
                    const isSourceDropdown = target.closest('.source-dropdown');
                    
                    // Check if clicking on the action buttons container div itself (empty space)
                    const actionButtonsContainer = target.closest('.customer-action-buttons');
                    const isOnContainerDiv = actionButtonsContainer && target === actionButtonsContainer;
                    
                    // If clicking on a button or source dropdown, don't open modal
                    if (isButton || isSourceDropdown) {
                      return; // Let the buttons handle their own clicks
                    }
                    
                    // If clicking on empty space within the action buttons container,
                    // open modal (this catches clicks that would be stopped by container's stopPropagation)
                    if (isOnContainerDiv) {
                      setSelectedCustomer(customer);
                      setIsCustomerDetailsModalOpen(true);
                      return;
                    }
                    
                    // For all other clicks, open modal (this will be caught in bubble phase if not stopped)
                    // But we also handle it here in capture to ensure it works
                    setSelectedCustomer(customer);
                    setIsCustomerDetailsModalOpen(true);
                  }}
                  onClick={(e) => {
                    // Ignore clicks from portal-rendered elements (e.g. CustomerMessageModal)
                    if (!e.currentTarget.contains(e.target as Node)) {
                      return;
                    }
                    
                    // Fallback handler for clicks that make it through (not stopped by container)
                    const target = e.target as HTMLElement;
                    
                    // Check if clicking directly on a button element
                    const clickedButton = target.closest('button');
                    const isButton = target.tagName === 'BUTTON' || 
                                    clickedButton ||
                                    (target.tagName === 'svg' && clickedButton) ||
                                    (target.tagName === 'path' && clickedButton);
                    const isSourceDropdown = target.closest('.source-dropdown');
                    
                    // If clicking on a button or source dropdown, don't open modal
                    if (isButton || isSourceDropdown) {
                      return;
                    }
                    
                    // For clicks that weren't handled in capture phase, open modal
                    setSelectedCustomer(customer);
                    setIsCustomerDetailsModalOpen(true);
                  }}
                  className="cursor-pointer"
                >
                  <BookingCustomerInfo
                    customer={customer}
                    canEdit={true}
                    mobile_booking={false}
                    businessId={selectedBusiness?.id}
                    statusBadge={
                      activeSegment?.visible ? (
                        <ActiveCustomerBadge active={activeHits.has(customer.id)} />
                      ) : undefined
                    }
                    onEdit={() => {
                      setSelectedCustomer(customer);
                      setIsCustomerDetailsModalOpen(true);
                    }}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {!loading && totalCount > 0 && (
            <div className="flex items-center justify-between gap-3 mt-4 px-1">
              <span className="text-[13px] text-gray-500" style={{ letterSpacing: '-0.01em' }}>
                {`${(page - 1) * PAGE_SIZE + 1}\u2013${Math.min(page * PAGE_SIZE, totalCount)} of ${totalCount}`}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-4 min-h-[40px] text-[14px] font-semibold rounded-[10px] border border-gray-200/60 bg-white text-gray-700 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed active:bg-gray-50 transition-all duration-150 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  Previous
                </button>
                <span className="text-[13px] text-gray-600 tabular-nums px-1" style={{ letterSpacing: '-0.01em' }}>
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-4 min-h-[40px] text-[14px] font-semibold rounded-[10px] border border-gray-200/60 bg-white text-gray-700 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed active:bg-gray-50 transition-all duration-150 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
        ) : (
          <div className="overflow-hidden rounded-[12px] border border-gray-200/60 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
            {listedSegments.length === 0 ? (
              <div className="px-4 py-10 text-center text-[15px] text-gray-500">
                {searchQuery ? 'No segments match that search.' : 'No segments yet.'}
              </div>
            ) : listedSegments.map((segment) => {
              const open = openSegmentId === segment.id;
              const count = segmentTotals[segment.id];
              const detail = [
                count === undefined ? '' : `${count} ${segmentCountLabel(segment, count)}`,
                segment.is_lead_list ? 'Lead list' : '',
                segment.visible ? '' : 'Hidden',
              ].filter(Boolean).join(' · ');
              const openPages = Math.max(1, Math.ceil(openTotal / PAGE_SIZE));
              return (
                <div key={segment.id} className="border-b border-gray-100 last:border-b-0">
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => void toggleSegment(segment)}
                      className="flex min-h-[64px] flex-1 items-center gap-3 px-4 py-3 text-left touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <ChevronDown className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-0' : '-rotate-90'}`} />
                      <span className="min-w-0">
                        <span className="block text-[15px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>{segment.name}</span>
                        {detail && <span className="block text-[13px] text-gray-500">{detail}</span>}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => { setEditingSegment(segment); setEditorOpen(true); }}
                      className="mr-3 rounded-full px-3 py-1.5 text-[13px] font-semibold text-gray-500 active:bg-gray-100"
                    >
                      Edit
                    </button>
                  </div>
                  {open && (
                    <div className="space-y-3 px-3 pb-3">
                      {openLoading ? (
                        <div className="py-8 text-center text-[14px] text-gray-500">Loading…</div>
                      ) : openRows.length === 0 ? (
                        <div className="py-8 text-center text-[14px] text-gray-500">No one in this segment.</div>
                      ) : openRows.map((customer) => (
                        <div
                          key={customer.id}
                          className="cursor-pointer"
                          onClick={(e) => {
                            const target = e.target as HTMLElement;
                            if (target.closest('button') || target.closest('.source-dropdown')) return;
                            setSelectedCustomer(customer);
                            setIsCustomerDetailsModalOpen(true);
                          }}
                        >
                          <BookingCustomerInfo
                            customer={customer}
                            canEdit={true}
                            mobile_booking={false}
                            businessId={selectedBusiness?.id}
                            onEdit={() => {
                              setSelectedCustomer(customer);
                              setIsCustomerDetailsModalOpen(true);
                            }}
                          />
                        </div>
                      ))}
                      {!openLoading && openTotal > PAGE_SIZE && (
                        <div className="flex items-center justify-end gap-2 px-1">
                          <button
                            type="button"
                            disabled={openPage <= 1}
                            onClick={() => void changeOpenPage(segment, openPage - 1)}
                            className="rounded-[10px] border border-gray-200/60 bg-white px-3 py-1.5 text-[13px] font-semibold text-gray-700 disabled:opacity-40"
                          >
                            Previous
                          </button>
                          <span className="text-[13px] text-gray-500">{openPage} of {openPages}</span>
                          <button
                            type="button"
                            disabled={openPage >= openPages}
                            onClick={() => void changeOpenPage(segment, openPage + 1)}
                            className="rounded-[10px] border border-gray-200/60 bg-white px-3 py-1.5 text-[13px] font-semibold text-gray-700 disabled:opacity-40"
                          >
                            Next
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            <button
              type="button"
              onClick={() => { setEditingSegment(null); setEditorOpen(true); }}
              className="w-full px-4 py-3.5 text-left text-[15px] font-semibold text-blue-600 active:bg-gray-50"
            >
              New segment
            </button>
          </div>
        )}
        </div>
      </div>
      <CustomerModal
        isOpen={isNewCustomerModalOpen}
        onClose={() => setIsNewCustomerModalOpen(false)}
        onSuccess={() => {
          setIsNewCustomerModalOpen(false);
          loadCustomers();
        }}
        businessId={selectedBusiness?.id || ''}
      />

      <CustomerDetailsModal
        isOpen={isCustomerDetailsModalOpen}
        onClose={() => {
          setIsCustomerDetailsModalOpen(false);
          setSelectedCustomer(null);
        }}
        customer={selectedCustomer}
        onUpdate={async () => {
          await loadCustomers();
          // Update selectedCustomer with fresh data if modal is still open
          if (selectedCustomer?.id) {
            try {
              const freshCustomer = await getCustomer(selectedCustomer.id);
              setSelectedCustomer(freshCustomer);
            } catch (err) {
              console.error('Error refreshing selected customer:', err);
            }
          }
        }}
      />

      {editorOpen && selectedBusiness?.id && (
        <SegmentEditor
          businessId={selectedBusiness.id}
          segment={editingSegment}
          hiddenActive={activeSegment && !activeSegment.visible ? activeSegment : null}
          onShowActive={async () => {
            if (!activeSegment) return;
            await saveCustomerSegment(selectedBusiness.id, {
              id: activeSegment.id,
              name: activeSegment.name,
              isLeadList: false,
              visible: true,
              rules: activeSegment.rules.length ? activeSegment.rules : [{ position: 0, kind: 'invoice_within_days', operator: 'has', value: { days: 45 } }],
              systemKey: 'active_customers',
            });
            await refreshSegments(activeSegment.id);
            setEditorOpen(false);
          }}
          onClose={() => setEditorOpen(false)}
          onSave={async (input) => {
            const id = await saveCustomerSegment(selectedBusiness.id, {
              id: editingSegment?.id,
              name: input.name,
              isLeadList: input.isLeadList,
              visible: input.visible,
              rules: input.rules,
              systemKey: editingSegment?.system_key || null,
            });
            await refreshSegments(id);
            setEditorOpen(false);
          }}
          onDelete={editingSegment && !editingSegment.system_key ? async () => {
            await deleteCustomerSegment(selectedBusiness.id, editingSegment.id);
            await refreshSegments();
            setEditorOpen(false);
          } : undefined}
        />
      )}

      <CustomerImportPreviewModal
        isOpen={isPreviewModalOpen}
        onClose={() => {
          setIsPreviewModalOpen(false);
          setParsedCustomers([]);
        }}
        customers={parsedCustomers}
        onImport={handleExecuteImport}
        businessId={selectedBusiness?.id || ''}
      />

      {/* CSV Import Modal - iOS Style */}
      {isImportModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10000] p-4"
             style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
          <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-xl max-w-md w-full overflow-hidden"
               style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif' }}>
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200/60 bg-white flex justify-between items-center">
              <h2 
                className="text-xl font-bold text-gray-900"
                style={{ letterSpacing: '-0.01em' }}
              >
                Import Customers
              </h2>
              <button 
                onClick={toggleImportModal}
                className="flex items-center justify-center w-11 h-11 rounded-[10px] text-gray-400 active:bg-gray-100 transition-all duration-150 touch-manipulation"
                style={{ 
                  minHeight: '44px',
                  minWidth: '44px',
                  WebkitTapHighlightColor: 'transparent'
                }}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 sm:p-6">
              <p className="text-[15px] text-gray-600 mb-6" style={{ letterSpacing: '-0.01em' }}>
                Upload a CSV file with customer information. The system will automatically detect the format.
              </p>
              
              {isParsing ? (
                <div className="mb-6 text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
                  <p className="text-[15px] font-semibold text-gray-700" style={{ letterSpacing: '-0.01em' }}>
                    Parsing CSV with AI...
                  </p>
                  <p className="text-[13px] text-gray-500 mt-2" style={{ letterSpacing: '-0.01em' }}>
                    This may take a few moments
                  </p>
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <label className="block text-[15px] font-semibold text-gray-700 mb-2" style={{ letterSpacing: '-0.01em' }}>CSV File</label>
                    <div className="flex items-center space-x-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="px-4 py-2.5 text-[15px] font-semibold text-gray-700 bg-white border border-gray-200/60 rounded-[10px] active:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-150 shadow-sm touch-manipulation flex-1"
                        style={{ 
                          minHeight: '44px',
                          WebkitTapHighlightColor: 'transparent',
                          letterSpacing: '-0.01em'
                        }}
                      >
                        {importFile ? importFile.name : 'Select CSV file'}
                      </button>
                      {importFile && (
                        <button
                          onClick={() => setImportFile(null)}
                          className="px-3 py-2.5 text-[15px] font-semibold text-gray-500 active:text-gray-700 active:bg-gray-100 rounded-[10px] transition-all duration-150 touch-manipulation"
                          style={{ 
                            minHeight: '44px',
                            WebkitTapHighlightColor: 'transparent',
                            letterSpacing: '-0.01em'
                          }}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200/60 p-4 rounded-[12px] mb-6">
                    <h3 className="text-[15px] font-semibold text-gray-700 mb-2" style={{ letterSpacing: '-0.01em' }}>🤖 AI-Powered CSV Parsing</h3>
                    <p className="text-[13px] text-gray-600 mb-2" style={{ letterSpacing: '-0.01em' }}>
                      Our AI will automatically parse your CSV file, regardless of format. It will:
                    </p>
                    <ul className="text-[13px] text-gray-600 space-y-1 mb-2" style={{ letterSpacing: '-0.01em' }}>
                      <li>• Detect column structure automatically</li>
                      <li>• Normalize phone numbers and emails</li>
                      <li>• Extract only valid customers</li>
                      <li>• Handle any CSV format (comma, semicolon, tab)</li>
                    </ul>
                    <p className="text-[13px] text-blue-600 font-medium" style={{ letterSpacing: '-0.01em' }}>
                      💡 After parsing, you'll review the results before importing
                    </p>
                  </div>
                  
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={toggleImportModal}
                      disabled={isParsing}
                      className="px-6 py-2.5 text-[15px] font-semibold text-gray-600 bg-gray-100 active:bg-gray-200 rounded-[10px] transition-all duration-150 shadow-sm touch-manipulation disabled:opacity-50"
                      style={{ 
                        minHeight: '44px',
                        WebkitTapHighlightColor: 'transparent',
                        letterSpacing: '-0.01em'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleParseCSV}
                      type="button"
                      disabled={!importFile || isParsing}
                      className="px-6 py-2.5 text-[15px] font-semibold text-white bg-blue-600 active:bg-blue-700 rounded-[10px] transition-all duration-150 shadow-sm touch-manipulation disabled:opacity-50"
                      style={{ 
                        minHeight: '44px',
                        WebkitTapHighlightColor: 'transparent',
                        letterSpacing: '-0.01em'
                      }}
                    >
                      {isParsing ? 'Parsing...' : 'Parse CSV'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Wrap with PermissionGuard
function CustomersWithPermission() {
  return (
    <PermissionGuard permission="customers.pageAccess">
      <Customers />
    </PermissionGuard>
  );
}

export default CustomersWithPermission;